import React from 'react';
import _, { compact } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { View, ScrollView, Platform, Image, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import HeaderContainer from '../../../components/shared/Header/Header.container';
import WebHeader from '../../../components/shared/Header/WebHeader.component';
import FiltersContainer, {
	FilterData,
} from '../../../components/web/Filters/Filters.container';
import colors from '../../../config/colors';
import httpService from '../../../services/http.service';
import { i18nService } from '../../../services/i18n.service';
import { getUserData } from '../../../store/selectors/login.selectors';
import HideViewButtons from './HideViewButtons/HideViewButtons.component';
import MonthlyData from './MonthlyData/MonthlyData.component';
import Report101 from './Form101/Form101.component';
import YearlyData from './YearlyData/YearlyData.component';
import { getSelectedUnits } from '../rep.utils';
import ReportFromMonthToMonth from './ReportFromMonthToMonth/ReportFromMonthToMonth.component';
import { Privileges, Role } from '../../../constants/roleTypes.constant';
import useEffectNotInitial from '../../../hooks/useEffectNotInitial';
import I18n from '../../../components/shared/i18n/I18n.component';
import {
	getOperatorIdList,
	getOperators,
} from '../../../store/selectors/operators.selector';
import { fetchOperatorsList } from '../../../store/actions/operators.actions';
import { getTotalHours } from '../../../store/selectors/workingTime.selector';
import { fetchClientsAndCompanysData } from '../../../store/actions/workingTime.action';
import { InAppMessages } from '../../../components/shared/InAppMessages/InAppMessages';
const calcImage = require('../../../../assets/images/calc/mini_calc_image.png');
//const calcImage = require('../../../../assets/images/calc/mini_calc.png');

const initialDisplay = {
	monthly: true,
	yearly: false,
	employeeForm: false,
	workPaperDraft: false,
};

const RepHomePage = ({ navigation }: any) => {
	const [displayCardMap, setDisplayCardMap] = useState<{
		monthly: boolean;
		yearly: boolean;
		employeeForm: boolean;
		workPaperDraft: boolean;
	}>();
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [filters, setFilters] = useState<any>({
		repCustomerIds: [],
		operatorIds: [],
		unitIds: [],
	});
	const [totalCompanysCount, setTotalCompanysCount] = useState<number | null>(
		null,
	);
	const dispatch = useDispatch();
	const userData = useSelector(getUserData);
	const operators = useSelector(getOperators);
	const accountantsIds = useSelector(getOperatorIdList);
	const totalHours = useSelector(getTotalHours);

	const filtersRef = useRef<any>();
	filtersRef.current = filters;
	const businessUnitsListRef = useRef<any>();
	businessUnitsListRef.current = businessUnitsList;
	const repCustomerIdsFullDataRef = useRef<any>();

	const customersFullDataJson = useMemo(
		() => JSON.stringify(repCustomerIdsFullDataRef.current || []),
		[repCustomerIdsFullDataRef.current],
	);

	const dashboardFilter = useMemo(
		() => ({
			...filters,
			unitIds: getSelectedUnits(filters.unitIds, businessUnitsList),
		}),
		[filters],
	);

	const canDisplay = useMemo(
		() => ({
			yearly:
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_FORM_106_REPORT_READ,
				) ||
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_FORM_106_REPORT_WRITE,
				) ||
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_ORG_126_REPORT_READ,
				) ||
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_ORG_126_REPORT_WRITE,
				),
			monthly:
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_PAYCHECK_REPORT_READ,
				) ||
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_PAYCHECK_REPORT_WRITE,
				),
			employeeForm:
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_FORM_101_REPORT_READ,
				) ||
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_FORM_101_REPORT_WRITE,
				),
			workPaperDraft:
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_WORK_PAPER_REPORT_READ,
				) ||
				userData?.repRolePrivileges?.includes(
					Privileges.ROLE_REP_WORK_PAPER_REPORT_WRITE,
				),
		}),
		[userData?.repRolePrivileges],
	);

	const getBusinessUnitsList = async () => {
		const res: any = await httpService.api<string[]>({
			type: 'getRepresntUnits',
			query: { pageName: 'repCustomers' },
		});
		setBusinessUnitsList(
			res?.map((item: any) => ({
				...item,
				id: item.numericId,
				stringId: item.id,
			})),
		);
		setFilters(state => ({
			...state,
			unitIds: [res[0]?.numericId],
		}));
	};

	const onFilter = (data: FilterData) => {
		if (data.name === 'unitIds') {
			setFilters({
				unitIds: [data.value],
				repCustomerIds: [],
				operatorIds: [],
			});
		} else if (data.name === 'repCustomerIds') {
			repCustomerIdsFullDataRef.current = data.value;
			const chosenIds = data.value.map((val: any) => val.id || val);
			setFilters({
				...filters,
				repCustomerIds: chosenIds?.includes?.('all') ? [] : chosenIds,
			});
		} else if (data.name === 'operatorIds') {
			const chosenIds = data.value.map((val: any) => val.id || val);
			setFilters({
				...filters,
				operatorIds: chosenIds?.includes?.('all') ? [] : chosenIds,
			});
		}
	};

	useEffectNotInitial(() => {
		sessionStorage.setItem('repDashboardFilters', JSON.stringify(filters));
	}, [filters]);

	useEffectNotInitial(() => {
		sessionStorage.setItem(
			'repDashboardCustomersFullData',
			customersFullDataJson,
		);
	}, [customersFullDataJson]);

	const getDisplayView = async () => {
		const res: any = await httpService.api({ type: 'getRepDashboardView' });
		if (res) {
			setDisplayCardMap(res);
		} else {
			setDisplayCardMap({
				...initialDisplay,
			});
		}
	};

	const onDisplayViewChange = async (
		type: 'monthly' | 'yearly' | 'employeeForm' | 'workPaperDraft',
		status: boolean,
	) => {
		const res: any = await httpService.api({
			type: 'updateRepDashboardView',
			params: { type: _.kebabCase(type) },
			data: { status },
		});
		setDisplayCardMap(state => ({
			...(state || initialDisplay),
			[type]: status,
		}));
	};

	useEffect(() => {
		const tempFilters = sessionStorage.getItem('repDashboardFilters');
		if (tempFilters && tempFilters !== '') {
			setFilters(JSON.parse(tempFilters));
		}
		const repDashboardCustomersFullData = sessionStorage.getItem(
			'repDashboardCustomersFullData',
		);
		repCustomerIdsFullDataRef.current = repDashboardCustomersFullData
			? JSON.parse(repDashboardCustomersFullData)
			: [];
		getDisplayView();
	}, []);

	useEffect(() => {
		if (businessUnitsList.length && filters.unitIds?.length) {
			getTotalCompanysCount(); // Fetch count only once per unitIds
		}
	}, [filters.unitIds, businessUnitsList]);

	const operatorsList = useMemo(() => {
		return [
			{
				id: -1,
				name: i18nService.translate(
					'repCustomersPage.operator.noManager',
				),
			},
			...operators,
		];
	}, [operators]);

	const totalAccountantsCount = operators.length;

	const getOperatorList = async () => {
		dispatch(
			fetchOperatorsList(
				getSelectedUnits(filters.unitIds, businessUnitsList),
			),
		);
	};

	const getTotalHoursWorked = async () => {
		dispatch(fetchClientsAndCompanysData(accountantsIds));
	};

	useEffect(() => {
		getBusinessUnitsList();
	}, [userData]);

	useEffect(() => {
		if (
			businessUnitsList.length &&
			filters.unitIds?.length &&
			accountantsIds.length
		) {
			getTotalHoursWorked();
		}
	}, [filters.unitIds, businessUnitsList, accountantsIds]);

	useEffect(() => {
		if (businessUnitsList.length && filters.unitIds?.length) {
			getOperatorList();
		}
	}, [filters.unitIds, businessUnitsList]);

	const getTotalCompanysCount = async () => {
		const res: any = await httpService.api({
			type: 'getRepCustomers',
			data: {
				p: 0,
				ps: 1,
				search: '',
				f: {
					unitIds: getSelectedUnits(
						filters.unitIds,
						businessUnitsList,
					),
				},
			},
			disableBI: true,
		});

		if (res?.total !== undefined) {
			setTotalCompanysCount(res.total);
		}
	};

	useEffect(() => {
		if (businessUnitsList.length && filters.unitIds?.length) {
			getTotalCompanysCount();
		}
	}, [filters.unitIds, businessUnitsList]);

	const getRepCustomerIdsData = async (
		search: string,
		loadedOptions: null,
		{ page }: { page: number },
	) => {
		const res: any = await httpService.api({
			type: 'getRepCustomers',
			data: {
				p: page,
				ps: 15,
				search,
				f: {
					unitIds: getSelectedUnits(
						filtersRef.current?.unitIds,
						businessUnitsListRef.current,
					),
				},
			},
			disableBI: true,
		});

		return {
			options: (page === 0
				? [
						{
							value: 'all',
							label: i18nService.translate('general.all'),
						},
				  ]
				: []
			).concat(
				res?.data?.map((el: any) => {
					return {
						value: el.numericId,
						label: el.name + ` (${el.id})`,
					};
				}),
			),
			hasMore: res?.hasNext,
			additional: {
				page: page + 1,
			},
		};
	};

	const filtersConfig = useMemo(
		() =>
			compact([
				{
					name: 'operatorIds',
					type: 'multiselect',
					options: operatorsList,
					value: filters.operatorIds?.map(id =>
						operatorsList.find(item => item.id === id),
					),
					placeholder: 'operators',
					styles: { 
						width: 223,
						clearIndicator: (base: any) => ({
							...base,
							padding: 6,
							visibility: 'visible',
						}),
					},
					dashboardViewMode: true,
				},
				{
					name: 'repCustomerIds',
					loadOptions: getRepCustomerIdsData,
					value: repCustomerIdsFullDataRef.current?.map((d: any) => ({
						label: d.name,
						value: d.id,
					})),
					key: `${JSON.stringify(filters.unitIds)}`,
					type: 'asyncmultiselect',
					placeholder: 'customers',
					styles: { width: 223 },
					dashboardViewMode: true,
				},
				businessUnitsList?.length > 1
					? {
							name: 'unitIds',
							type: 'select',
							placeholder: 'businessUnits',
							defaultValue: filters.unitIds?.[0],
							options: businessUnitsList,
							hideDefaultOption: true,
							styles: {
								width: 200,
							},
					  }
					: undefined,
			]),
		[
			operatorsList,
			businessUnitsList,
			filters.unitIds,
			filters.operatorIds,
			repCustomerIdsFullDataRef.current,
		],
	);

	return !displayCardMap ? null : (
		<>
			<InAppMessages userRole={Role.REPRESENTATIVE}/>
			
			<View style={{ width: '100%', flex: 1 }}>
				<HeaderContainer
					containerStyle={{ backgroundColor: colors.white }}
					navigation={navigation}
					component={WebHeader}
				/>

				<ScrollView
					style={{ flex: 1 }}
					contentContainerStyle={styles.wrapper}
				>
					<View style={styles.topContainer}>
						<View
							style={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'center',
								gap: 16,
								paddingHorizontal: 16,
							}}
						>
							<View>
								<I18n
									size={19}
									weight='400'
									color={colors.titlePrimary}
								>
									{i18nService.translate('repDashboard.title')}
								</I18n>
								<I18n
									size={19}
									weight='bold'
									color={colors.titlePrimary}
								>
									{i18nService.translate('repDashboard.subTitle')}
								</I18n>
							</View>
							<Image
								source={calcImage}
								style={{
									height: 66,
									width: 49,
								}}
							/>
						</View>

						<View
							style={{
								display: 'flex',
								flexDirection: 'row-reverse',
								flex: 1,
								alignItems: 'center',
								justifyContent: 'center',
								gap: 32,
								paddingHorizontal: 16,
							}}
						>
							<View
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-end',
									gap: 8,
									width: 80,
								}}
							>
								<I18n
									size={14}
									weight='400'
									color={colors.subtitleColor}
								>
									{i18nService.translate('repDashboard.accountants')}
								</I18n>
								<I18n
									size={22}
									weight='bold'
									color={colors.darkGrey}
								>
									{totalAccountantsCount}
								</I18n>
							</View>

							<View
								style={{
									width: 1,
									height: 80,
									backgroundColor: colors.greySeparator,
								}}
							></View>

							<View
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-end',
									gap: 8,
								}}
							>
								<I18n
									size={14}
									weight='400'
									color={colors.subtitleColor}
								>
									{i18nService.translate('repDashboard.handeledCompanies')}
								</I18n>
								<I18n
									size={22}
									weight='bold'
									color={colors.darkGrey}
								>
									{totalCompanysCount}
								</I18n>
							</View>

							<View
								style={{
									width: 1,
									height: 80,
									backgroundColor: colors.greySeparator,
								}}
							></View>

							<View
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-end',
									gap: 8,
								}}
							>
								<I18n
									size={14}
									weight='400'
									color={colors.subtitleColor}
								>
									{i18nService.translate('repDashboard.totalHours')}
								</I18n>
								<I18n
									size={22}
									weight='bold'
									color={colors.darkGrey}
								>
									{totalHours}
								</I18n>
							</View>
						</View>
					</View>

					<View style={styles.midContainer}>
						<View
							style={{
								zIndex: 100,
								display: 'flex',
								flexDirection: 'row-reverse',
								alignItems: 'center',
							}}
						>
							<FiltersContainer
								config={filtersConfig}
								onChange={onFilter}
								customStyles={{
									container: {
										flexDirection: 'row-reverse',
									},
									filterContainer: {
										marginRight: 0,
										marginLeft: 15,
										zIndex: 3,
										flexDirection: 'row-reverse',
										alignItems: 'center',
									},
								}}
							/>
							<HideViewButtons
								displayCardMap={displayCardMap}
								setDisplayCardMap={onDisplayViewChange}
								canDisplay={canDisplay}
							/>
						</View>
						{displayCardMap.monthly && canDisplay.monthly && (
							<MonthlyData
								filters={dashboardFilter}
								customersFullDataJson={customersFullDataJson}
							/>
						)}
						<View
							style={{
								flexDirection: 'row-reverse',
								...Platform.select({ web: { gap: 24 } }),
							}}
						>
							{displayCardMap.employeeForm &&
								canDisplay.employeeForm && (
									<Report101
										filters={dashboardFilter}
										customersFullDataJson={
											customersFullDataJson
										}
									/>
								)}
							{displayCardMap.workPaperDraft &&
								canDisplay.workPaperDraft && (
									<ReportFromMonthToMonth
										filters={dashboardFilter}
										customersFullDataJson={
											customersFullDataJson
										}
									/>
								)}
						</View>
						{displayCardMap.yearly && canDisplay.yearly && (
							<YearlyData
								filters={dashboardFilter}
								customersFullDataJson={customersFullDataJson}
							/>
						)}
					</View>
				</ScrollView>
			</View>
		</>
	);
};

export default RepHomePage;

const styles = StyleSheet.create({
	wrapper: {
		width: '100%',
		paddingHorizontal: 32,
		paddingVertical: 24,
		gap: 24,
	},
	topContainer: {
		flexDirection: 'row-reverse',
		alignItems: 'flex-end',
		padding: 24,
		gap: 16,
		backgroundColor: colors.white,
		borderRadius: 5,
	},
	midContainer: {
		width: '100%',
		paddingBottom: 120,
	},
});
